import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { ManualIngestionCell } from '../components/ManualIngestionCell';
import { ManualReviewActions } from '../components/ManualReviewActions';
import { StatusBadge } from '../components/StatusBadge';
import { CRAWL_POLL_MS, isRunStale, type CrawlRun } from '../crawlRuns';
import { formatBytes, formatDate, show } from '../format';

// Give up tracking a crawl after this long WITHOUT a successful status read (the
// run is created synchronously, so the first read normally lands in <1s — a long
// gap means the status endpoint is failing: an expired session, a 5xx, etc.). We
// then stop polling and surface the error instead of leaving the trigger stuck on
// "Scanning…". Comfortably longer than the poll interval so a brief blip is ridden
// out; far shorter than the stale-run cutoff, which is about a worker that never
// finishes, not a status read we can't make.
const CRAWL_STATUS_TIMEOUT_MS = 60_000;

interface DeviceInstance {
  boat_id: string;
  boat_name: string;
  device_id: string;
  protocol: string;
  software_version: string | null;
  model_version: string | null;
  serial_code: string | null;
  device_label: string | null;
  // Per-unit user preferred name (ADR 0052). Shown read-only and separately
  // from device_label; never folds into the shared catalog name.
  user_preferred_name: string | null;
  installation_description_1: string | null;
  installation_description_2: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

interface Manual {
  id: string;
  title: string;
  revision: string | null;
  doc_number: string | null;
  is_installation_only: boolean | null;
  language: string | null;
  source: 'upload' | 'crawl';
  source_url: string | null;
  content_type: string | null;
  original_filename: string | null;
  size_bytes: number | null;
  sha256: string | null;
  status: 'published' | 'suggested' | 'rejected';
  // Crawl-review fields (task device_manual_crawl, ADR 0073). Null for an
  // admin upload.
  confidence: number | null;
  crawl_run_id: string | null;
  created_at: string;
  download_url: string | null;
}

interface AdminDeviceDetailData {
  model: {
    id: string;
    manufacturer_code: number | null;
    manufacturer_name: string | null;
    product_code: number | null;
    model_id: string | null;
    device_class_code: number | null;
    device_class_name: string | null;
    device_function_code: number | null;
    device_function_name: string | null;
    // Admin override (editable) + the resolved catalog name (ADR 0052).
    preferred_name: string | null;
    // Provenance of preferred_name (ADR 0080): 'llm' (AI-inferred), 'manual'
    // (admin edit), or null (no stored name → algorithmic inference).
    preferred_name_source: 'llm' | 'manual' | null;
    name_inferred_at: string | null;
    display_name: string;
    created_at: string;
    updated_at: string;
  };
  user_count: number;
  boat_count: number;
  instance_count: number;
  firmware_versions: string[];
  users: Array<{
    user_id: string;
    email: string;
    tier: string;
    instances: DeviceInstance[];
  }>;
  manuals: Manual[];
}

// Inline icons for the inline name actions (Edit / Re-infer). Feather/Lucide
// style: 24×24, stroke=currentColor so they inherit the teal link color and
// the disabled/hover states. aria-hidden — the parent button carries the label.
function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

// Arrows-rotate / refresh = "re-run the inference".
function RotateIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function modelTitle(m: AdminDeviceDetailData['model']): string {
  const mfr =
    m.manufacturer_name ?? (m.manufacturer_code !== null ? `#${m.manufacturer_code}` : '');
  const model =
    m.model_id ?? (m.product_code !== null ? `Product ${m.product_code}` : 'Unknown model');
  return [mfr, model].filter((s) => s.length > 0).join(' ');
}

// Build an informative message for a failed re-infer. The server returns a
// structured `{ message, requestId }` for the failures it knows about (503 key
// missing, 502 LLM error / no usable name, 404). When it does NOT — an edge/proxy
// timeout, a 5xx with no JSON body, or no response at all (network) — fall back
// to the HTTP status + a hint, or a connection message, so the operator can tell
// a misconfig from a timeout from an outage instead of a bare "failed".
function describeReinferError(err: unknown): string {
  const e = err as { status?: number; body?: unknown; message?: string };
  const body = (e.body ?? null) as { message?: unknown; requestId?: unknown } | null;
  // Most specific: the app's JSON error envelope.
  if (body !== null && typeof body === 'object' && typeof body.message === 'string') {
    const ref = typeof body.requestId === 'string' ? ` (ref: ${body.requestId})` : '';
    return `${body.message}${ref}`;
  }
  // An HTTP error with no JSON body — usually a gateway / proxy / edge layer.
  if (typeof e.status === 'number') {
    const hint =
      e.status === 503
        ? 'AI naming isn’t configured on this environment.'
        : e.status === 504 || e.status === 524
          ? 'the AI request timed out — naming can be slow; try again.'
          : e.status === 502
            ? 'the AI service was unreachable; try again.'
            : e.status === 429
              ? 'rate limited; wait a moment and try again.'
              : e.status >= 500
                ? 'a server error occurred; try again.'
                : 'the request was rejected.';
    return `Couldn’t infer a name (HTTP ${e.status}) — ${hint}`;
  }
  // No HTTP response at all (network error / connection dropped).
  const detail = typeof e.message === 'string' && e.message.length > 0 ? ` (${e.message})` : '';
  return `Couldn’t reach the server to infer a name${detail}. Check your connection and try again.`;
}

export function AdminDeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [data, setData] = useState<AdminDeviceDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Inline edit of the shared catalog preferred name (admin-only).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // AI re-infer of the catalog name (task device_name_llm, ADR 0080).
  const [reinferring, setReinferring] = useState(false);
  // Result feedback: a real change is a teal 'success', a no-op is muted 'info',
  // a failure is a sticky 'error'. Transient (success/info) auto-dismisses.
  const [reinferNotice, setReinferNotice] = useState<{
    kind: 'success' | 'info' | 'error';
    text: string;
  } | null>(null);
  // Inline arm→fire guard before a re-infer overwrites a hand-typed name
  // (matches ManualReviewActions rather than a native confirm()).
  const [confirmingReinfer, setConfirmingReinfer] = useState(false);
  const reinferTimer = useRef<number | null>(null);
  // Manuals upload form state.
  const [manualFile, setManualFile] = useState<File | null>(null);
  const [manualTitle, setManualTitle] = useState('');
  const [manualSourceUrl, setManualSourceUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  // Per-model AI crawl trigger (task device_manual_crawl, ADR 0073). `crawling`
  // covers only the POST itself; once it's accepted we track the run it started
  // (by id) and poll its status so the page shows live "Scanning…" → completion
  // rather than the old fire-and-forget "refresh in a moment" note.
  const [crawling, setCrawling] = useState(false);
  // id of the crawl we just kicked off and are now following live (null = none).
  const [trackedRunId, setTrackedRunId] = useState<string | null>(null);
  // Latest fetched state of that run (status + counters), or null until the
  // first status poll lands.
  const [crawlRun, setCrawlRun] = useState<CrawlRun | null>(null);
  // A status-poll failure: a soft "retrying…" note while we keep polling, or a
  // sticky terminal message once we give up (so the trigger never stays stuck on
  // "Scanning…" with no feedback when the status read keeps failing).
  const [crawlError, setCrawlError] = useState<string | null>(null);
  // Wall-clock (ms) of the last SUCCESSFUL status read — seeded when tracking
  // starts. If reads keep failing past CRAWL_STATUS_TIMEOUT_MS we stop tracking
  // and surface the error, instead of polling a dead endpoint forever. A ref (not
  // state) so updating it doesn't itself re-render.
  const lastStatusOkRef = useRef<number>(0);
  // Fallback note for the (effectively unreachable) case where the 202 carries
  // no run id — preserves the old "refresh in a moment" behavior.
  const [crawlNotice, setCrawlNotice] = useState<string | null>(null);

  const loadData = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    try {
      const res = await adminApi<AdminDeviceDetailData>(`/v1/admin/devices/${id}`);
      setData(res);
      setError(null);
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      if (e.status === 404) {
        setError('Device model not found.');
      } else {
        setError(e.body?.message ?? 'Failed to load device.');
      }
    }
  }, [id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // When something deep-links into a section of this page, scroll it into view
  // once the data is loaded and the section is mounted. The review queue links
  // to #attach-manual (upload a replacement); the devices list links to
  // #manuals (the manuals table) from its Manual-column icon.
  useEffect(() => {
    if (data === null) return;
    const target = location.hash.slice(1);
    if (target !== 'attach-manual' && target !== 'manuals') return;
    document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
  }, [location.hash, data]);

  // Clear any pending auto-dismiss timer on unmount.
  useEffect(
    () => () => {
      if (reinferTimer.current !== null) window.clearTimeout(reinferTimer.current);
    },
    []
  );

  // The tracked crawl is "active" while we're following a run that's still
  // running and hasn't gone stale (or before its first status poll lands). This
  // is a stable boolean across counter-only updates, so the polling effect below
  // doesn't tear down its interval every tick — it only flips on a real
  // start→finish (or freshness) transition.
  const trackedRunActive =
    trackedRunId !== null &&
    (crawlRun === null || (crawlRun.status === 'running' && !isRunStale(crawlRun.started_at)));

  // While the crawl we kicked off is active, poll its status on a timer (an
  // audit-exempt read) so this page reflects "Scanning…" and then a completion
  // result — without the operator refreshing. We fetch OUR specific run BY ID:
  // scanning the capped recent-runs list would lose track of it once a burst of
  // auto-crawl runs (one per newly-discovered SKU) pushes it off that page.
  useEffect(() => {
    if (!trackedRunActive || trackedRunId === null) return;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const res = await adminApi<{ run: CrawlRun }>(
          `/v1/admin/manuals/crawl-runs/${trackedRunId}`
        );
        if (cancelled) return;
        lastStatusOkRef.current = Date.now();
        setCrawlRun(res.run);
        setCrawlError(null);
      } catch (err) {
        if (cancelled) return;
        // A status read failed. Stop (and re-enable the trigger) when it CANNOT
        // self-resolve — a 4xx (e.g. an expired session 401, or the run gone 404)
        // — or when reads have failed for too long. Otherwise keep retrying, but
        // record the failure so the page re-renders (which re-checks the stale
        // cutoff) and the operator sees we're retrying rather than a frozen button.
        const status = (err as { status?: number }).status;
        const is4xx = typeof status === 'number' && status >= 400 && status < 500;
        const timedOut = Date.now() - lastStatusOkRef.current > CRAWL_STATUS_TIMEOUT_MS;
        if (is4xx || timedOut) {
          setCrawlError(
            status === 401
              ? 'Your admin session expired — reload the page to sign in and check the scan.'
              : 'Couldn’t fetch the scan status. The scan may still be running; reload to check.'
          );
          setTrackedRunId(null); // stop tracking → re-enables the trigger
        } else {
          setCrawlError('Couldn’t refresh the scan status — retrying…');
        }
      }
    };
    void poll();
    const handle = window.setInterval(() => void poll(), CRAWL_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [trackedRunActive, trackedRunId]);

  // Reload the device once when the tracked run reaches a TERMINAL status, so any
  // newly-suggested manuals appear in the table below. Keyed off the transition
  // INTO terminal (from null or 'running') rather than "was running" — a fast or
  // no-op run can finish before any poll ever observes 'running', and that must
  // still trigger the reload.
  const crawlPrevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = crawlRun?.status ?? null;
    const prev = crawlPrevStatusRef.current;
    const wasTerminal = prev !== null && prev !== 'running';
    const isTerminal = status !== null && status !== 'running';
    if (isTerminal && !wasTerminal) void loadData();
    crawlPrevStatusRef.current = status;
  }, [crawlRun, loadData]);

  // Drop the re-infer notice (and its auto-dismiss timer). A bare clear; a
  // transient set schedules its own dismissal.
  function clearReinferNotice(): void {
    if (reinferTimer.current !== null) {
      window.clearTimeout(reinferTimer.current);
      reinferTimer.current = null;
    }
    setReinferNotice(null);
  }

  // Fold a server-resolved name update into the loaded model (shared by the
  // manual save and the AI re-infer, which return the same fields).
  function applyResolvedName(fields: Partial<AdminDeviceDetailData['model']>): void {
    setData((prev) => (prev === null ? prev : { ...prev, model: { ...prev.model, ...fields } }));
  }

  async function uploadManual(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (id === undefined || manualFile === null) return;
    setUploading(true);
    setManualError(null);
    try {
      const form = new FormData();
      // Text fields first so the server parses them before the file part.
      if (manualTitle.trim().length > 0) form.append('title', manualTitle.trim());
      if (manualSourceUrl.trim().length > 0) form.append('source_url', manualSourceUrl.trim());
      form.append('file', manualFile);
      const res = await fetch(`/v1/admin/devices/${id}/manuals`, {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      if (!res.ok) {
        let message = 'Failed to upload manual.';
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === 'string' && body.message.length > 0) message = body.message;
        } catch {
          // non-JSON error body — keep the default message.
        }
        throw new Error(message);
      }
      setManualFile(null);
      setManualTitle('');
      setManualSourceUrl('');
      await loadData();
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to upload manual.');
    } finally {
      setUploading(false);
    }
  }

  async function crawlThisModel(): Promise<void> {
    if (id === undefined) return;
    setManualError(null);
    setCrawlNotice(null);
    setCrawlError(null);
    // Drop any prior run we were showing so a fresh scan starts from a clean
    // status (otherwise the previous "Scan complete" lingers under the new run).
    setCrawlRun(null);
    setTrackedRunId(null);
    setCrawling(true);
    try {
      const res = await adminApi<{ accepted: boolean; crawlRunId: string | null }>(
        '/v1/admin/manuals/crawl',
        { method: 'POST', body: { deviceModelId: id } }
      );
      // Follow the run live: the polling effect picks up trackedRunId and shows
      // "Scanning…" until it finishes, then a completion status. The 202 always
      // carries an id; the null branch is a defensive fallback to the old note.
      if (res.crawlRunId !== null) {
        // Seed the "last good status" clock so the give-up timeout measures from
        // the start of tracking, not from epoch.
        lastStatusOkRef.current = Date.now();
        setTrackedRunId(res.crawlRunId);
      } else {
        setCrawlNotice('Crawl started. Refresh in a moment to see any suggestions.');
      }
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      setManualError(
        e.body?.message ??
          (e.status === 503
            ? 'Manual crawl is not configured on this environment.'
            : 'Failed to start the crawl.')
      );
    } finally {
      setCrawling(false);
    }
  }

  function startEdit(current: string | null): void {
    setDraft(current ?? '');
    setSaveError(null);
    // Don't carry a stale re-infer result (or an armed confirm) into editing.
    setConfirmingReinfer(false);
    clearReinferNotice();
    setEditing(true);
  }

  async function savePreferredName(): Promise<void> {
    if (id === undefined) return;
    const trimmed = draft.trim();
    setSaving(true);
    setSaveError(null);
    try {
      const res = await adminApi<{
        ok: true;
        preferred_name: string | null;
        preferred_name_source: 'llm' | 'manual' | null;
        display_name: string;
      }>(`/v1/admin/devices/${id}`, {
        method: 'PATCH',
        body: { preferredName: trimmed.length > 0 ? trimmed : null },
      });
      // Optimistic-ish: fold the server's resolved values back into state. A
      // manual save clears the (now-stale) LLM inference timestamp.
      applyResolvedName({
        preferred_name: res.preferred_name,
        preferred_name_source: res.preferred_name_source,
        name_inferred_at: null,
        display_name: res.display_name,
      });
      clearReinferNotice();
      setEditing(false);
    } catch (err) {
      const e = err as { body?: { message?: string } };
      setSaveError(e.body?.message ?? 'Failed to save preferred name.');
    } finally {
      setSaving(false);
    }
  }

  // Re-infer button handler. A re-infer is allowed to replace a hand-typed name
  // (ADR 0080), but not on a stray click — when the current name is 'manual' we
  // arm an inline confirm first (matching ManualReviewActions, not a native
  // dialog). Otherwise re-infer straight away.
  function requestReinfer(): void {
    if (data === null) return;
    clearReinferNotice();
    const m = data.model;
    if (
      m.preferred_name_source === 'manual' &&
      m.preferred_name !== null &&
      m.preferred_name.length > 0
    ) {
      setConfirmingReinfer(true);
      return;
    }
    void doReinfer();
  }

  // Run the inference and fold the result back into state. The server returns a
  // human-readable message; a real change is teal 'success', a no-op muted
  // 'info', a failure a sticky 'error'.
  async function doReinfer(): Promise<void> {
    if (id === undefined) return;
    setConfirmingReinfer(false);
    clearReinferNotice();
    setReinferring(true);
    try {
      const res = await adminApi<{
        ok: true;
        changed: boolean;
        preferred_name: string | null;
        preferred_name_source: 'llm' | 'manual' | null;
        name_inferred_at: string | null;
        display_name: string;
        message: string;
      }>(`/v1/admin/devices/${id}/infer-name`, { method: 'POST' });
      applyResolvedName({
        preferred_name: res.preferred_name,
        preferred_name_source: res.preferred_name_source,
        name_inferred_at: res.name_inferred_at,
        display_name: res.display_name,
      });
      // Transient feedback: auto-dismiss after a few seconds so a no-op result
      // never lingers as stale chrome.
      setReinferNotice({ kind: res.changed ? 'success' : 'info', text: res.message });
      reinferTimer.current = window.setTimeout(() => setReinferNotice(null), 6000);
    } catch (err) {
      // Errors are sticky (no auto-dismiss) so the operator can act on them.
      setReinferNotice({ kind: 'error', text: describeReinferError(err) });
    } finally {
      setReinferring(false);
    }
  }

  // Live status for the crawl we kicked off: a status badge + a one-line summary
  // that moves from "Scanning…" to a completion result. Null until a crawl is
  // started, so the strip only shows once there's something to report.
  const crawlStatus = ((): { badge: string; text: string } | null => {
    if (trackedRunId === null) return null;
    // POST accepted; the first status poll hasn't landed yet.
    if (crawlRun === null) {
      return { badge: 'running', text: 'Scanning the web for this model’s manuals…' };
    }
    if (crawlRun.status === 'running') {
      return isRunStale(crawlRun.started_at)
        ? {
            badge: 'running',
            text: 'Scan is taking unusually long — the worker may have stopped. Try “Find manuals (AI)” again.',
          }
        : { badge: 'running', text: 'Scanning the web for this model’s manuals…' };
    }
    if (crawlRun.status === 'failed') {
      return {
        badge: 'failed',
        text: `Scan failed${crawlRun.error !== null ? `: ${crawlRun.error}` : ''}. Try again.`,
      };
    }
    // Succeeded. For a single-model crawl manuals_suggested is this model's new
    // suggestions; they were just folded into the table by the reload effect.
    const n = crawlRun.manuals_suggested;
    return {
      badge: 'succeeded',
      text:
        n > 0
          ? `Scan complete — found ${n} new manual${n === 1 ? '' : 's'} to review below.`
          : 'Scan complete — no new manual suggestions filed for this model.',
    };
  })();

  return (
    <section className="admin-section">
      <p>
        <Link to="/admin/devices">← Back to devices</Link>
      </p>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
      {data === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {data !== null ? (
        <>
          <header className="admin-section-header">
            <h1>{data.model.display_name}</h1>
            <span className="admin-count">
              {data.user_count} {data.user_count === 1 ? 'user' : 'users'} · {data.boat_count}{' '}
              {data.boat_count === 1 ? 'boat' : 'boats'} · {data.instance_count}{' '}
              {data.instance_count === 1 ? 'unit' : 'units'}
            </span>
          </header>

          <dl className="admin-defs">
            <dt>Preferred name</dt>
            <dd>
              {editing ? (
                <span className="admin-inline-edit">
                  <input
                    type="text"
                    value={draft}
                    maxLength={120}
                    placeholder="Custom display name"
                    disabled={saving}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void savePreferredName();
                      if (e.key === 'Escape') setEditing(false);
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="admin-button-affirm"
                    onClick={() => void savePreferredName()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="admin-button-secondary"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  {saveError !== null ? (
                    <span className="admin-inline-error">{saveError}</span>
                  ) : null}
                  <span className="admin-hint">Blank reverts to the inferred name.</span>
                </span>
              ) : (
                <>
                  {data.model.preferred_name !== null && data.model.preferred_name.length > 0 ? (
                    <>
                      {data.model.preferred_name}{' '}
                      <span
                        className={`admin-badge admin-badge-${
                          data.model.preferred_name_source === 'llm' ? 'ai' : 'manual'
                        }`}
                      >
                        {data.model.preferred_name_source === 'llm' ? 'AI-inferred' : 'Manual'}
                      </span>
                    </>
                  ) : (
                    <em>{data.model.display_name} (inferred)</em>
                  )}{' '}
                  {confirmingReinfer ? (
                    <span className="admin-confirm">
                      <span className="admin-confirm-prompt">
                        Replace the manual name “{data.model.preferred_name}” with a fresh AI
                        suggestion from this model’s manuals and details?
                      </span>
                      <button
                        type="button"
                        className="admin-button-affirm"
                        onClick={() => void doReinfer()}
                        disabled={reinferring}
                      >
                        {reinferring ? 'Inferring…' : 'Re-infer'}
                      </button>
                      <button
                        type="button"
                        className="admin-button-secondary"
                        onClick={() => setConfirmingReinfer(false)}
                        disabled={reinferring}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <span className="admin-icon-actions">
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => startEdit(data.model.preferred_name)}
                        disabled={reinferring}
                        aria-label="Edit preferred name"
                        title="Edit name"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="admin-icon-button"
                        onClick={() => requestReinfer()}
                        disabled={reinferring}
                        aria-label="Re-infer name with AI"
                        aria-busy={reinferring}
                        title={
                          reinferring
                            ? 'Inferring…'
                            : 'Re-infer name with AI — suggests a name from this model’s manuals and catalog details'
                        }
                      >
                        <RotateIcon />
                      </button>
                    </span>
                  )}
                  {data.model.preferred_name_source === 'llm' &&
                  data.model.name_inferred_at !== null ? (
                    <div className="admin-reinfer-asof admin-run-strip-updated">
                      Inferred {formatDate(data.model.name_inferred_at)}
                    </div>
                  ) : null}
                  <div className="admin-reinfer-live" role="status" aria-live="polite">
                    {reinferNotice !== null ? (
                      <span className={`admin-reinfer-notice is-${reinferNotice.kind}`}>
                        {reinferNotice.text}
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </dd>
            <dt>SKU</dt>
            <dd>{modelTitle(data.model)}</dd>
            <dt>Manufacturer</dt>
            <dd>
              {show(data.model.manufacturer_name)}
              {data.model.manufacturer_code !== null
                ? ` (code ${data.model.manufacturer_code})`
                : ''}
            </dd>
            <dt>Model</dt>
            <dd>{show(data.model.model_id)}</dd>
            <dt>Product code</dt>
            <dd>{data.model.product_code !== null ? data.model.product_code : '—'}</dd>
            <dt>Device class</dt>
            <dd>
              {show(data.model.device_class_name)}
              {data.model.device_class_code !== null ? ` (${data.model.device_class_code})` : ''}
            </dd>
            <dt>Device function</dt>
            <dd>
              {show(data.model.device_function_name)}
              {data.model.device_function_code !== null
                ? ` (${data.model.device_function_code})`
                : ''}
            </dd>
            <dt>Firmware in the field</dt>
            <dd className="admin-chips">
              {data.firmware_versions.length === 0
                ? '—'
                : data.firmware_versions.map((v) => <code key={v}>{v}</code>)}
            </dd>
          </dl>

          <h2 id="manuals">Manuals</h2>
          <p className="admin-hint">
            Product manuals (PDF) attached to this shared model and visible to every owner. Two ways
            to add one: let AI suggest candidates (you review each before it publishes), or upload a
            PDF yourself below (it publishes immediately).
          </p>
          <div className="admin-action-bar">
            <button
              type="button"
              className="admin-button-secondary"
              onClick={() => void crawlThisModel()}
              disabled={crawling || trackedRunActive}
            >
              {crawling ? 'Starting…' : trackedRunActive ? 'Scanning…' : 'Find manuals (AI)'}
            </button>
            <p className="admin-hint admin-action-caption">
              Searches the web for this model’s manuals — any matches appear below as “suggested”
              for you to review before publishing.
            </p>
          </div>
          {crawlStatus !== null ? (
            <div className="admin-run-strip" role="status" aria-live="polite">
              <StatusBadge status={crawlStatus.badge} />
              <span>{crawlStatus.text}</span>
              {/* A transient status-read failure while still tracking — a muted
                  "retrying…" cue next to the live status. */}
              {crawlError !== null ? <span className="admin-hint">{crawlError}</span> : null}
            </div>
          ) : crawlError !== null ? (
            // We stopped tracking on a status-read failure we couldn't recover.
            <div className="admin-run-strip" role="status" aria-live="polite">
              <StatusBadge status="failed" />
              <span>{crawlError}</span>
            </div>
          ) : crawlNotice !== null ? (
            <p className="admin-hint">{crawlNotice}</p>
          ) : null}
          {data.manuals.some((m) => m.status === 'suggested') ? (
            <p className="admin-hint">
              <Link to="/admin/manuals">Review AI suggestions across all models →</Link>
            </p>
          ) : null}
          {data.manuals.length === 0 ? (
            <p className="admin-empty">No manuals attached yet.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Revision</th>
                    <th>Status</th>
                    <th>Search index</th>
                    <th>Size</th>
                    <th>Source page</th>
                    <th>File</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.manuals.map((m) => (
                    <tr key={m.id} data-testid={`admin-manual-row-${m.id}`}>
                      <td>
                        {m.title}
                        {m.is_installation_only ? ' (installation)' : ''}
                      </td>
                      <td>{show(m.revision)}</td>
                      <td>
                        <StatusBadge status={m.status} />
                        {m.status === 'suggested' && m.confidence !== null
                          ? ` ${Math.round(m.confidence * 100)}%`
                          : ''}
                      </td>
                      <td>
                        <ManualIngestionCell manualId={m.id} published={m.status === 'published'} />
                      </td>
                      <td>{formatBytes(m.size_bytes)}</td>
                      <td>
                        {m.source_url !== null && m.source_url.length > 0 ? (
                          <a href={m.source_url} target="_blank" rel="noreferrer">
                            Source page
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {m.download_url !== null ? (
                          // Audit is derived server-side from the manual's status
                          // (suggested preview = no-audit, published download =
                          // audited; ADR 0079), so the link needs no query flag.
                          <a href={m.download_url} target="_blank" rel="noreferrer">
                            {m.status === 'suggested' ? 'Preview PDF' : 'Download'}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <ManualReviewActions
                          manualId={m.id}
                          deviceModelId={id}
                          actions={m.status === 'suggested' ? ['approve', 'reject'] : ['delete']}
                          onDone={() => void loadData()}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form
            id="attach-manual"
            className="admin-manual-upload"
            onSubmit={(e) => void uploadManual(e)}
          >
            <h3>Upload a manual</h3>
            <p className="admin-hint admin-action-caption">
              Upload a PDF you already have — it publishes immediately to all owners of this model
              (no review needed).
            </p>
            <div className="admin-field">
              <label htmlFor="manual-file">PDF file</label>
              <input
                id="manual-file"
                type="file"
                accept="application/pdf,.pdf"
                disabled={uploading}
                onChange={(e) => setManualFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="admin-field">
              <label htmlFor="manual-title">Title (optional)</label>
              <input
                id="manual-title"
                type="text"
                value={manualTitle}
                maxLength={200}
                placeholder="Defaults to the file name"
                disabled={uploading}
                onChange={(e) => setManualTitle(e.target.value)}
              />
            </div>
            <div className="admin-field">
              <label htmlFor="manual-source-url">Source URL (optional)</label>
              <input
                id="manual-source-url"
                type="url"
                value={manualSourceUrl}
                maxLength={2000}
                placeholder="https://manufacturer.example/manual.pdf"
                disabled={uploading}
                onChange={(e) => setManualSourceUrl(e.target.value)}
              />
            </div>
            <button type="submit" disabled={uploading || manualFile === null}>
              {uploading ? 'Uploading…' : 'Upload manual'}
            </button>
            {manualError !== null ? (
              <span className="admin-inline-error">{manualError}</span>
            ) : null}
          </form>

          <h2>Per-user breakdown</h2>
          {data.users.length === 0 ? (
            <p className="admin-empty">No active instances associated with any user.</p>
          ) : (
            data.users.map((u) => (
              <div
                key={u.user_id}
                className="admin-device-user"
                data-testid={`admin-device-user-${u.user_id}`}
              >
                <h3>
                  <Link to={`/admin/users/${u.user_id}`}>{u.email}</Link>{' '}
                  <span className="admin-count">{u.tier}</span>
                </h3>
                <div className="scroll-x">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Boat</th>
                        <th>Firmware</th>
                        <th>Model ver.</th>
                        <th>Serial</th>
                        <th>Label</th>
                        <th>User preferred name</th>
                        <th>Installation</th>
                        <th>First seen</th>
                        <th>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {u.instances.map((inst) => (
                        <tr key={`${inst.boat_id}:${inst.device_id}`}>
                          <td>{inst.boat_name}</td>
                          <td>{show(inst.software_version)}</td>
                          <td>{show(inst.model_version)}</td>
                          <td>{show(inst.serial_code)}</td>
                          <td>{show(inst.device_label)}</td>
                          <td>{show(inst.user_preferred_name)}</td>
                          <td>
                            {[inst.installation_description_1, inst.installation_description_2]
                              .filter((v) => v !== null && v.length > 0)
                              .join(' / ') || '—'}
                          </td>
                          <td>{formatDate(inst.first_seen_at)}</td>
                          <td>{formatDate(inst.last_seen_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </>
      ) : null}
    </section>
  );
}
